export function Charts(rawArray = []) {
  // Normalize and assign zone safely if missing
  const dataArray = (Array.isArray(rawArray) ? rawArray : []).map((sample, idx, arr) => {
    if (sample.zone) return sample;
    let zone = 'General';
    if (sample.location === 'Empresa Grupo Técnico Basse Ltda') {
      const sameTime = arr.filter(
        (s) => s.location === sample.location && s.date === sample.date
      );
      if (sameTime.length > 1) {
        const minCo = Math.min(...sameTime.map((s) => Number(s.co || 0)));
        zone = Number(sample.co || 0) === minCo ? 'Primer Piso' : 'Segundo piso';
      } else {
        zone = 'Primer Piso';
      }
    }
    return { ...sample, zone };
  });

  const getDayMonth = (dateStr) => {
    if (!dateStr) return '';
    try {
      const clean = String(dateStr).replace('Z', '').trim();
      const datePart = clean.includes('T') ? clean.split('T')[0] : clean.split(' ')[0];
      return datePart.replace(/^\d{4}-/, '');
    } catch {
      return '';
    }
  };

  const productionLabels = [
    "Primer Piso - 8:00",
    "Primer Piso - 12:00",
    "Primer Piso - 17:00",
    "Segundo piso - 8:00",
    "Segundo piso - 12:00",
    "Segundo piso - 17:00",
  ];

  const mean = (location, label, parameter, type = 0) => {
    let dataFiltered;
    const parts = (label || "").split(" - ");
    const zone = parts[0] || "";
    const date = parts[1] || "";
    if (type === 0) {
      dataFiltered = dataArray.filter(
        (sample) =>
          location === sample.location &&
          (sample.zone ? sample.zone.toLowerCase().includes(zone.toLowerCase()) : true) &&
          (sample.date ? String(sample.date).includes(date) : false)
      );
    } else {
      dataFiltered = dataArray.filter(
        (sample) =>
          location === sample.location &&
          (sample.date ? String(sample.date).includes(date) : false)
      );
    }
    if (!dataFiltered.length) return 0;
    const media =
      dataFiltered
        .map((item) => Number(item[parameter] || 0))
        .reduce((sum, value) => sum + value, 0) / dataFiltered.length;
    return Number(media.toFixed(2));
  };

  const meanByDate = (data, parameter, location) => {
    const grouped = {};
    data.forEach((item) => {
      if (item.location !== location) return;
      const dateKey = getDayMonth(item.date);
      if (!dateKey) return;
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(Number(item[parameter] || 0));
    });
    return Object.values(grouped).map(
      (values) =>
        values.reduce((sum, v) => sum + v, 0) / (values.length || 1)
    );
  };

  const linearRegression = (data) => {
    const n = data.length;
    if (n === 0) return { slope: 0, intercept: 0 };
    const sumX = data.reduce((acc, p) => acc + p.x, 0);
    const sumY = data.reduce((acc, p) => acc + p.y, 0);
    const sumXY = data.reduce((acc, p) => acc + p.x * p.y, 0);
    const sumXX = data.reduce((acc, p) => acc + p.x * p.x, 0);
    const denom = n * sumXX - sumX * sumX;
    if (denom === 0) return { slope: 0, intercept: sumY / n };
    const slope = (n * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / n;
    return { slope, intercept };
  };

  const scatterData = (location, zone) => {
    return dataArray
      .filter(
        (sample) =>
          sample.location === location &&
          (sample.zone ? sample.zone.toLowerCase() === zone.toLowerCase() : true)
      )
      .map((sample) => ({
        x: Number(sample.co || 0),
        y: Number(sample.nox || 0),
      }));
  };

  const trendlineData = (location, zone) => {
    const points = scatterData(location, zone);
    if (!points || points.length === 0) return [];
    const { slope, intercept } = linearRegression(points);
    if (isNaN(slope) || isNaN(intercept)) return [];
    const minX = Math.min(...points.map((p) => p.x));
    const maxX = Math.max(...points.map((p) => p.x));
    return [
      { x: minX, y: Number((slope * minX + intercept).toFixed(2)) },
      { x: maxX, y: Number((slope * maxX + intercept).toFixed(2)) },
    ];
  };

  return [
    {
      type: 'line',
      key: "Dashboard",
      data: {
        labels: [
          ...new Set(
            dataArray
              .filter(({ location }) => location === 'Empresa Grupo Técnico Basse Ltda')
              .map(({ date }) => getDayMonth(date))
              .filter(Boolean)
          ),
        ],
        datasets: [
          {
            label: 'CO',
            data: meanByDate(dataArray, "co", "Empresa Grupo Técnico Basse Ltda"),
            borderColor: '#fbcfe8',
            backgroundColor: '#fbcfe8',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
          {
            label: 'NOX',
            data: meanByDate(dataArray, "nox", "Empresa Grupo Técnico Basse Ltda"),
            borderColor: '#bae6fd',
            backgroundColor: '#bae6fd',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Evolución de Concentraciones de CO y NOX' },
          legend: { position: 'top' },
        },
        scales: {
          x: { title: { display: true, text: 'Fecha (día)' } },
          y: { title: { display: true, text: 'Concentración de (ppm)' } },
        },
      },
    },
    {
      title: "Promedio de Concentración de Contaminantes por Zona y Horario",
      location: "",
      type: "bar",
      class: "col-span-2",
      key: "Empresa Grupo Técnico Basse Ltda",
      data: {
        labels: productionLabels,
        datasets: [
          {
            label: "CO",
            data: productionLabels.map((label) => mean('Empresa Grupo Técnico Basse Ltda', label, 'co')),
            backgroundColor: "rgba(75,192,192,0.5)",
          },
          {
            label: "NOx",
            data: productionLabels.map((label) => mean('Empresa Grupo Técnico Basse Ltda', label, 'nox')),
            backgroundColor: "rgba(255,99,132,0.5)",
          },
        ],
      },
      options: {
        scales: {
          y: {
            title: {
              display: true,
              text: "Concentración (ppm)",
            },
            beginAtZero: true,
            ticks: {
              stepSize: 2,
            },
          },
          x: {
            ticks: {
              maxRotation: 45,
              minRotation: 45,
            },
          },
        },
      },
    },
    {
      title: "Distribucion de CO por zona",
      type: 'boxplot',
      key: "Empresa Grupo Técnico Basse Ltda",
      data: {
        labels: ['Primer Piso', 'Segundo Piso'],
        datasets: [
          {
            label: 'CO',
            backgroundColor: 'rgba(75,192,192,0.5)',
            borderColor: 'rgba(75,192,192,1)',
            borderWidth: 1,
            outlierColor: '#999999',
            padding: 10,
            itemRadius: 0,
            data: [
              dataArray.filter(({ location, zone }) => location === 'Empresa Grupo Técnico Basse Ltda' && zone === 'Primer Piso').map(({ co }) => Number(co)),
              dataArray.filter(({ location, zone }) => location === 'Empresa Grupo Técnico Basse Ltda' && zone === 'Segundo piso').map(({ co }) => Number(co)),
            ],
          },
        ],
      },
    },
    {
      title: "Distribucion de NOX por zona",
      type: 'boxplot',
      key: "Empresa Grupo Técnico Basse Ltda",
      data: {
        labels: ['Primer Piso', 'Segundo Piso'],
        datasets: [
          {
            label: 'CO',
            backgroundColor: 'rgba(75,192,192,0.5)',
            borderColor: 'rgba(75,192,192,1)',
            borderWidth: 1,
            outlierColor: '#999999',
            padding: 10,
            itemRadius: 0,
            data: [
              dataArray.filter(({ location, zone }) => location === 'Empresa Grupo Técnico Basse Ltda' && zone === 'Primer Piso').map(({ nox }) => Number(nox)),
              dataArray.filter(({ location, zone }) => location === 'Empresa Grupo Técnico Basse Ltda' && zone === 'Segundo piso').map(({ nox }) => Number(nox)),
            ],
          },
        ],
      },
    },
    {
      type: 'scatter',
      key: "Empresa Grupo Técnico Basse Ltda",
      data: {
        datasets: [
          {
            label: 'Datos',
            data: scatterData('Empresa Grupo Técnico Basse Ltda', 'Primer Piso'),
            backgroundColor: 'rgba(75,192,192,0.6)',
          },
          {
            label: 'Línea de tendencia',
            type: 'line',
            data: trendlineData('Empresa Grupo Técnico Basse Ltda', 'Primer Piso'),
            borderColor: 'red',
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Relación CO vs NOX - Primer Piso' },
        },
        scales: {
          x: { title: { display: true, text: 'CO (ppm)' } },
          y: { title: { display: true, text: 'NOX (ppm)' } },
        },
      },
    },
    {
      type: 'scatter',
      key: "Empresa Grupo Técnico Basse Ltda",
      data: {
        datasets: [
          {
            label: 'Datos',
            data: scatterData('Empresa Grupo Técnico Basse Ltda', 'Segundo piso'),
            backgroundColor: 'rgba(75,192,192,0.6)',
          },
          {
            label: 'Línea de tendencia',
            type: 'line',
            data: trendlineData('Empresa Grupo Técnico Basse Ltda', 'Segundo piso'),
            borderColor: 'red',
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Relación CO vs NOX - Segundo piso' },
        },
        scales: {
          x: { title: { display: true, text: 'CO (ppm)' } },
          y: { title: { display: true, text: 'NOX (ppm)' } },
        },
      },
    },
    {
      type: 'line',
      key: "Pruebas Piloto Portal Suba",
      data: {
        labels: dataArray.filter(({ location }) => location === 'Portal Suba').map(({ date }) => getDayMonth(date)).filter(Boolean),
        datasets: [
          {
            label: 'CO (ppm)',
            data: dataArray.filter(({ location }) => location === 'Portal Suba').map(({ co }) => Number(co)),
            borderColor: 'rgba(75,192,192,0.6)',
            backgroundColor: 'rgba(75,192,192,0.6)',
            fill: false,
            tension: 0.2,
            spanGaps: true,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Serie de tiempo de CO - Portal Suba' },
          legend: { position: 'top' },
        },
        scales: {
          x: { title: { display: true, text: 'Fecha (día)' } },
          y: { title: { display: true, text: 'Concentración de CO (ppm)' } },
        },
      },
    },
    {
      type: 'line',
      key: "Pruebas Piloto Portal Suba",
      data: {
        labels: dataArray.filter(({ location }) => location === 'Portal Suba').map(({ date }) => getDayMonth(date)).filter(Boolean),
        datasets: [
          {
            label: 'NOX (ppm)',
            data: dataArray.filter(({ location }) => location === 'Portal Suba').map(({ nox }) => Number(nox)),
            borderColor: 'rgba(75,192,192,0.6)',
            backgroundColor: 'rgba(75,192,192,0.6)',
            fill: false,
            tension: 0.2,
            spanGaps: true,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Serie de tiempo de NOX - Portal Suba' },
          legend: { position: 'top' },
        },
        scales: {
          x: { title: { display: true, text: 'Fecha (día)' } },
          y: { title: { display: true, text: 'Concentración de NOX (ppm)' } },
        },
      },
    },
    {
      type: 'line',
      key: "Pruebas Piloto Portal Suba",
      data: {
        labels: [
          ...new Set(
            dataArray
              .filter(({ location }) => location === 'Portal Suba')
              .map(({ date }) => getDayMonth(date))
              .filter(Boolean)
          ),
        ],
        datasets: [
          {
            label: '08:00',
            data: dataArray.filter(({ location, date }) => location === 'Portal Suba' && String(date).includes('08:00')).map(({ co }) => Number(co)),
            borderColor: '#e41a1c',
            backgroundColor: '#e41a1c',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
          {
            label: '12:00',
            data: dataArray.filter(({ location, date }) => location === 'Portal Suba' && String(date).includes('12:00')).map(({ co }) => Number(co)),
            borderColor: '#377eb8',
            backgroundColor: '#377eb8',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
          {
            label: '18:00',
            data: dataArray.filter(({ location, date }) => location === 'Portal Suba' && String(date).includes('18:00')).map(({ co }) => Number(co)),
            borderColor: '#4daf4a',
            backgroundColor: '#4daf4a',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Concentración de CO - Portal Suba' },
          legend: { position: 'top' },
        },
        scales: {
          x: { title: { display: true, text: 'Fecha (día)' } },
          y: { title: { display: true, text: 'Concentración de CO (ppm)' } },
        },
      },
    },
    {
      type: 'line',
      key: "Pruebas Piloto Portal Suba",
      data: {
        labels: [
          ...new Set(
            dataArray
              .filter(({ location }) => location === 'Portal Suba')
              .map(({ date }) => getDayMonth(date))
              .filter(Boolean)
          ),
        ],
        datasets: [
          {
            label: '08:00',
            data: dataArray.filter(({ location, date }) => location === 'Portal Suba' && String(date).includes('08:00')).map(({ nox }) => Number(nox)),
            borderColor: '#e41a1c',
            backgroundColor: '#e41a1c',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
          {
            label: '12:00',
            data: dataArray.filter(({ location, date }) => location === 'Portal Suba' && String(date).includes('12:00')).map(({ nox }) => Number(nox)),
            borderColor: '#377eb8',
            backgroundColor: '#377eb8',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
          {
            label: '18:00',
            data: dataArray.filter(({ location, date }) => location === 'Portal Suba' && String(date).includes('18:00')).map(({ nox }) => Number(nox)),
            borderColor: '#4daf4a',
            backgroundColor: '#4daf4a',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Concentración de NOX - Portal Suba' },
          legend: { position: 'top' },
        },
        scales: {
          x: { title: { display: true, text: 'Fecha (día)' } },
          y: { title: { display: true, text: 'Concentración de NOX (ppm)' } },
        },
      },
    },
    {
      type: 'line',
      key: "Pruebas Piloto Parqueadero Plaza Imperial",
      data: {
        labels: dataArray.filter(({ location }) => location === 'Parqueadero Plaza Imperial').map(({ date }) => getDayMonth(date)).filter(Boolean),
        datasets: [
          {
            label: 'CO (ppm)',
            data: dataArray.filter(({ location }) => location === 'Parqueadero Plaza Imperial').map(({ co }) => Number(co)),
            borderColor: 'rgba(75,192,192,0.6)',
            backgroundColor: 'rgba(75,192,192,0.6)',
            fill: false,
            tension: 0.2,
            spanGaps: true,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Serie de tiempo de CO - Parqueadero Plaza Imperial' },
          legend: { position: 'top' },
        },
        scales: {
          x: { title: { display: true, text: 'Fecha (día)' } },
          y: { title: { display: true, text: 'Concentración de CO (ppm)' } },
        },
      },
    },
    {
      type: 'line',
      key: "Pruebas Piloto Parqueadero Plaza Imperial",
      data: {
        labels: dataArray.filter(({ location }) => location === 'Parqueadero Plaza Imperial').map(({ date }) => getDayMonth(date)).filter(Boolean),
        datasets: [
          {
            label: 'NOX (ppm)',
            data: dataArray.filter(({ location }) => location === 'Parqueadero Plaza Imperial').map(({ nox }) => Number(nox)),
            borderColor: 'rgba(75,192,192,0.6)',
            backgroundColor: 'rgba(75,192,192,0.6)',
            fill: false,
            tension: 0.2,
            spanGaps: true,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Serie de tiempo de NOX - Parqueadero Plaza Imperial' },
          legend: { position: 'top' },
        },
        scales: {
          x: { title: { display: true, text: 'Fecha (día)' } },
          y: { title: { display: true, text: 'Concentración de NOX (ppm)' } },
        },
      },
    },
    {
      type: 'line',
      key: "Pruebas Piloto Parqueadero Plaza Imperial",
      data: {
        labels: [
          ...new Set(
            dataArray
              .filter(({ location }) => location === 'Parqueadero Plaza Imperial')
              .map(({ date }) => getDayMonth(date))
              .filter(Boolean)
          ),
        ],
        datasets: [
          {
            label: '08:00',
            data: dataArray.filter(({ location, date }) => location === 'Parqueadero Plaza Imperial' && String(date).includes('08:00')).map(({ co }) => Number(co)),
            borderColor: '#e41a1c',
            backgroundColor: '#e41a1c',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
          {
            label: '12:00',
            data: dataArray.filter(({ location, date }) => location === 'Parqueadero Plaza Imperial' && String(date).includes('12:00')).map(({ co }) => Number(co)),
            borderColor: '#377eb8',
            backgroundColor: '#377eb8',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
          {
            label: '18:00',
            data: dataArray.filter(({ location, date }) => location === 'Parqueadero Plaza Imperial' && String(date).includes('18:00')).map(({ co }) => Number(co)),
            borderColor: '#4daf4a',
            backgroundColor: '#4daf4a',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Concentración de CO - Parqueadero Plaza Imperial' },
          legend: { position: 'top' },
        },
        scales: {
          x: { title: { display: true, text: 'Fecha (día)' } },
          y: { title: { display: true, text: 'Concentración de CO (ppm)' } },
        },
      },
    },
    {
      type: 'line',
      key: "Pruebas Piloto Parqueadero Plaza Imperial",
      data: {
        labels: [
          ...new Set(
            dataArray
              .filter(({ location }) => location === 'Parqueadero Plaza Imperial')
              .map(({ date }) => getDayMonth(date))
              .filter(Boolean)
          ),
        ],
        datasets: [
          {
            label: '08:00',
            data: dataArray.filter(({ location, date }) => location === 'Parqueadero Plaza Imperial' && String(date).includes('08:00')).map(({ nox }) => Number(nox)),
            borderColor: '#e41a1c',
            backgroundColor: '#e41a1c',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
          {
            label: '12:00',
            data: dataArray.filter(({ location, date }) => location === 'Parqueadero Plaza Imperial' && String(date).includes('12:00')).map(({ nox }) => Number(nox)),
            borderColor: '#377eb8',
            backgroundColor: '#377eb8',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
          {
            label: '18:00',
            data: dataArray.filter(({ location, date }) => location === 'Parqueadero Plaza Imperial' && String(date).includes('18:00')).map(({ nox }) => Number(nox)),
            borderColor: '#4daf4a',
            backgroundColor: '#4daf4a',
            fill: false,
            tension: 0.1,
            pointRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: true, text: 'Concentración de NOX - Parqueadero Plaza Imperial' },
          legend: { position: 'top' },
        },
        scales: {
          x: { title: { display: true, text: 'Fecha (día)' } },
          y: { title: { display: true, text: 'Concentración de NOX (ppm)' } },
        },
      },
    },
  ];
}
